import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Users, 
  Briefcase, 
  TrendingUp, 
  TrendingDown,
  AlertCircle,
  ArrowUpRight,
  Calendar,
  CreditCard,
  Filter,
  Heart,
  AlertTriangle,
  CheckCircle2,
  Info,
  ChevronRight,
  Activity
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, RadialBarChart, RadialBar } from "recharts";
import OnboardingBanner from "../components/OnboardingBanner";

const formatCurrency = (amount, devise = "FCFA") => {
  if (devise === "EUR") {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(amount);
  }
  return new Intl.NumberFormat("fr-FR").format(amount) + " FCFA";
};

// Tooltip personnalisé pour les graphiques CA
const CustomCATooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const caValue = data?.ca ?? 0;
    const moisLabel = data?.mois || label || '';
    return (
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-lg">
        <p className="text-zinc-300 text-sm font-medium mb-1">{moisLabel}</p>
        <p className="text-white text-lg font-bold">{formatCurrency(caValue)}</p>
      </div>
    );
  }
  return null;
};

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const MONTHS = [
  { value: "all", label: "Tous les mois" },
  { value: "1", label: "Janvier" },
  { value: "2", label: "Février" },
  { value: "3", label: "Mars" },
  { value: "4", label: "Avril" },
  { value: "5", label: "Mai" },
  { value: "6", label: "Juin" },
  { value: "7", label: "Juillet" },
  { value: "8", label: "Août" },
  { value: "9", label: "Septembre" },
  { value: "10", label: "Octobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Décembre" },
];

const YEARS = Array.from({ length: 5 }, (_, i) => ({
  value: String(currentYear - i),
  label: String(currentYear - i)
}));

// Composant Score de Santé amélioré avec conseils
const HealthScore = ({ score, niveau, composants }) => {
  const getScoreColor = (s) => {
    if (s >= 80) return "text-emerald-400";
    if (s >= 60) return "text-blue-400";
    if (s >= 40) return "text-amber-400";
    return "text-red-400";
  };
  
  const getScoreGradient = (s) => {
    if (s >= 80) return "from-emerald-500/20 to-emerald-900/10";
    if (s >= 60) return "from-blue-500/20 to-blue-900/10";
    if (s >= 40) return "from-amber-500/20 to-amber-900/10";
    return "from-red-500/20 to-red-900/10";
  };
  
  // Conseils personnalisés selon le composant le plus faible
  const getConseil = () => {
    if (!composants || composants.length === 0) return null;
    const plusFaible = composants.reduce((min, c) => 
      (c.score / c.max) < (min.score / min.max) ? c : min
    );
    const ratio = plusFaible.score / plusFaible.max;
    
    if (plusFaible.nom === "Recouvrement" && ratio < 0.5) {
      return { text: "Relancez vos clients pour améliorer le recouvrement", icon: CreditCard };
    }
    if (plusFaible.nom === "Diversification" && ratio < 0.5) {
      return { text: "Diversifiez votre portefeuille clients", icon: Users };
    }
    if (plusFaible.nom === "Pipeline" && ratio < 0.5) {
      return { text: "Prospectez pour alimenter votre pipeline", icon: TrendingUp };
    }
    if (plusFaible.nom === "Ponctualité" && ratio < 0.5) {
      return { text: "Négociez de meilleures conditions de paiement", icon: Calendar };
    }
    return null;
  };
  
  const conseil = getConseil();

  return (
    <Card className={`bg-gradient-to-br ${getScoreGradient(score)} border-zinc-800 relative overflow-hidden`}>
      <div className="absolute top-0 right-0 w-32 h-32 opacity-5">
        <Heart className="w-full h-full" />
      </div>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-400 animate-pulse" />
            Santé Financière
          </CardTitle>
          <Badge className={`${getScoreColor(score)} bg-transparent border-current`}>
            {niveau?.toUpperCase()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-6">
          <div className="relative w-24 h-24 flex-shrink-0">
            <svg className="w-24 h-24 transform -rotate-90">
              <circle cx="48" cy="48" r="40" stroke="#27272A" strokeWidth="8" fill="none" />
              <circle 
                cx="48" cy="48" r="40" 
                stroke={score >= 80 ? "#10B981" : score >= 60 ? "#3B82F6" : score >= 40 ? "#F59E0B" : "#EF4444"}
                strokeWidth="8" 
                fill="none"
                strokeDasharray={`${score * 2.51} 251`}
                strokeLinecap="round"
                className="transition-all duration-1000"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-2xl font-bold ${getScoreColor(score)}`}>{score}</span>
            </div>
          </div>
          <div className="flex-1 space-y-2">
            {composants?.map((c, i) => (
              <div key={i} className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-400">{c.nom}</span>
                  <span className="text-zinc-300">{c.score}/{c.max}</span>
                </div>
                <Progress value={(c.score / c.max) * 100} className="h-1.5" />
              </div>
            ))}
          </div>
        </div>
        {conseil && (
          <div className="mt-4 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800 flex items-center gap-3">
            <conseil.icon className="w-4 h-4 text-blue-400 flex-shrink-0" />
            <span className="text-xs text-zinc-400">{conseil.text}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Composant Alertes Intelligentes amélioré
const AlertesWidget = ({ alertes, navigate }) => {
  const getAlertIcon = (type) => {
    switch(type) {
      case "warning": return <AlertTriangle className="w-4 h-4 text-amber-400" />;
      case "success": return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
      case "info": return <Info className="w-4 h-4 text-blue-400" />;
      case "danger": return <AlertCircle className="w-4 h-4 text-red-400" />;
      default: return <AlertCircle className="w-4 h-4 text-zinc-400" />;
    }
  };
  
  const getAlertBg = (type) => {
    switch(type) {
      case "warning": return "bg-amber-950/30 border-amber-800/50 hover:bg-amber-950/50";
      case "success": return "bg-emerald-950/30 border-emerald-800/50 hover:bg-emerald-950/50";
      case "info": return "bg-blue-950/30 border-blue-800/50 hover:bg-blue-950/50";
      case "danger": return "bg-red-950/30 border-red-800/50 hover:bg-red-950/50";
      default: return "bg-zinc-900/50 border-zinc-800 hover:bg-zinc-800/50";
    }
  };
  
  const getPriorityBadge = (priorite) => {
    if (priorite === "haute") return <Badge className="bg-red-500/20 text-red-400 text-[10px] px-1.5 py-0">URGENT</Badge>;
    if (priorite === "moyenne") return <Badge className="bg-amber-500/20 text-amber-400 text-[10px] px-1.5 py-0">À FAIRE</Badge>;
    return null;
  };

  // Si pas d'alertes, afficher un message positif
  if (!alertes || alertes.length === 0) {
    return (
      <Card className="bg-gradient-to-br from-emerald-950/20 to-[#121214] border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-400" />
            Alertes & Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/30 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-400" />
            <div>
              <p className="text-sm font-medium text-emerald-300">Tout va bien !</p>
              <p className="text-xs text-zinc-500 mt-0.5">Aucune action urgente requise</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Trier les alertes par priorité
  const sortedAlertes = [...alertes].sort((a, b) => {
    const priorityOrder = { haute: 0, moyenne: 1, info: 2 };
    return (priorityOrder[a.priorite] || 3) - (priorityOrder[b.priorite] || 3);
  });

  return (
    <Card className="bg-[#121214] border-zinc-800">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-400" />
            Alertes & Actions
          </CardTitle>
          <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400">
            {alertes.length} alerte{alertes.length > 1 ? 's' : ''}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {sortedAlertes.map((alerte, i) => (
          <div 
            key={i} 
            className={`p-3 rounded-lg border ${getAlertBg(alerte.type)} cursor-pointer transition-all duration-200 group`}
            onClick={() => navigate && navigate(alerte.action)}
          >
            <div className="flex items-start gap-3">
              {getAlertIcon(alerte.type)}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-sm font-medium text-white truncate">{alerte.titre}</p>
                  {getPriorityBadge(alerte.priorite)}
                </div>
                <p className="text-xs text-zinc-400 line-clamp-2">{alerte.message}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300 transition-colors flex-shrink-0" />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const [selectedMonth, setSelectedMonth] = useState("all");

  useEffect(() => {
    fetchDashboard();
  }, [selectedYear, selectedMonth]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("annee", selectedYear);
      if (selectedMonth !== "all") {
        params.append("mois", selectedMonth);
      }
      const response = await apiClient.get(`/dashboard?${params}`);
      setData(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement du dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Chargement...</div>
      </div>
    );
  }

  if (!data) return null;

  const { stats, evolution_ca, paiements_a_venir, paiements_retard, missions_recentes, predictions, sante_financiere, alertes } = data;

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Onboarding Banner */}
      <OnboardingBanner onDismiss={() => fetchDashboard()} />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white tracking-tight">Dashboard</h1>
          <p className="text-zinc-500 mt-1">Vue d'ensemble de votre activité</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Filtres Année et Mois */}
          <div className="flex items-center gap-2" data-testid="dashboard-filters">
            <Filter className="w-4 h-4 text-zinc-500" />
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[120px] bg-zinc-900/50 border-zinc-800">
                <SelectValue placeholder="Année" />
              </SelectTrigger>
              <SelectContent className="bg-[#121214] border-zinc-800">
                {YEARS.map((year) => (
                  <SelectItem key={year.value} value={year.value}>{year.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[150px] bg-zinc-900/50 border-zinc-800">
                <SelectValue placeholder="Mois" />
              </SelectTrigger>
              <SelectContent className="bg-[#121214] border-zinc-800">
                {MONTHS.map((month) => (
                  <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {predictions?.tendance === "hausse" ? (
            <Badge className="bg-emerald-900/50 text-emerald-300 border-emerald-800">
              <TrendingUp className="w-3 h-3 mr-1" />
              Tendance à la hausse
            </Badge>
          ) : (
            <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700">
              <TrendingDown className="w-3 h-3 mr-1" />
              Tendance stable
            </Badge>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-[#121214] border-zinc-800 card-hover kpi-card" data-testid="kpi-clients">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Clients</p>
                <p className="text-3xl font-heading font-bold text-white">{stats.clients}</p>
                <p className="text-sm text-zinc-500 mt-1">
                  {stats.leads} leads · {stats.prospects} prospects
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-600/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#121214] border-zinc-800 card-hover kpi-card" data-testid="kpi-missions">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Missions Actives</p>
                <p className="text-3xl font-heading font-bold text-white">{stats.missions_actives}</p>
                <p className="text-sm text-zinc-500 mt-1">En cours</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#121214] border-zinc-800 card-hover kpi-card" data-testid="kpi-ca">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">CA Total</p>
                <p className="text-2xl font-mono font-bold text-white">
                  {formatCurrency(stats.ca_total_fcfa)}
                </p>
                {stats.ca_total_eur > 0 && (
                  <p className="text-sm font-mono text-zinc-500 mt-1">
                    {formatCurrency(stats.ca_total_eur, "EUR")}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-lg bg-amber-600/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#121214] border-zinc-800 card-hover kpi-card" data-testid="kpi-conversion">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Taux Conversion</p>
                <p className="text-3xl font-heading font-bold text-white">{stats.taux_conversion}%</p>
                <p className="text-sm text-zinc-500 mt-1">Leads → Clients</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-violet-600/20 flex items-center justify-center">
                <ArrowUpRight className="w-6 h-6 text-violet-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-8 gap-4">
        {/* CA Evolution Chart */}
        <Card className="lg:col-span-5 bg-[#121214] border-zinc-800" data-testid="chart-evolution">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg text-white">
                Évolution du CA - {selectedYear}
                {selectedMonth !== "all" && ` (${MONTHS.find(m => m.value === selectedMonth)?.label})`}
              </CardTitle>
              {data?.periode && (
                <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-xs">
                  Total: {formatCurrency(data.periode.ca_periode)}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] min-h-[300px]">
              <ResponsiveContainer width="100%" height={300}>
                {selectedMonth === "all" ? (
                  <BarChart data={evolution_ca} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis dataKey="mois" stroke="#71717A" fontSize={12} />
                    <YAxis stroke="#71717A" fontSize={12} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : `${(v/1000).toFixed(0)}K`} />
                    <Tooltip content={<CustomCATooltip />} />
                    <Bar 
                      dataKey="ca" 
                      fill="#3B82F6" 
                      radius={[4, 4, 0, 0]}
                      name="ca"
                    />
                  </BarChart>
                ) : (
                  <LineChart data={evolution_ca} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis dataKey="jour" stroke="#71717A" fontSize={12} />
                    <YAxis stroke="#71717A" fontSize={12} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : `${(v/1000).toFixed(0)}K`} />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-lg">
                              <p className="text-zinc-300 text-sm font-medium mb-1">Jour {data?.jour || label}</p>
                              <p className="text-white text-lg font-bold">{formatCurrency(data?.ca ?? 0)}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="ca" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      dot={{ fill: '#3B82F6', strokeWidth: 0 }}
                      activeDot={{ r: 6, fill: '#3B82F6' }}
                    />
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Prédictions & Alertes */}
        <Card className="lg:col-span-3 bg-[#121214] border-zinc-800" data-testid="predictions-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white">Prévisions & Alertes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Prediction */}
            <div className="p-4 rounded-lg bg-blue-600/10 border border-blue-600/20">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-blue-300 uppercase tracking-wider">Prévision mois prochain</span>
              </div>
              <p className="text-2xl font-mono font-bold text-white">
                {formatCurrency(predictions.ca_prochain_mois)}
              </p>
            </div>

            {/* Alertes paiements en retard */}
            {stats.paiements_retard > 0 && (
              <div className="p-4 rounded-lg bg-red-900/20 border border-red-900/30">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-red-300 uppercase tracking-wider">Paiements en retard</span>
                </div>
                <p className="text-2xl font-heading font-bold text-white">{stats.paiements_retard}</p>
                <p className="text-sm text-red-300/70 mt-1">À relancer</p>
              </div>
            )}

            {/* CA Encaissé */}
            <div className="p-4 rounded-lg bg-emerald-900/20 border border-emerald-900/30">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-300 uppercase tracking-wider">CA Encaissé</span>
              </div>
              <p className="text-2xl font-mono font-bold text-white">
                {formatCurrency(stats.ca_encaisse_fcfa)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Prochains paiements */}
        <Card className="bg-[#121214] border-zinc-800" data-testid="paiements-a-venir">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-zinc-500" />
              Paiements à venir
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {paiements_a_venir.length === 0 ? (
                <p className="text-zinc-500 text-sm">Aucun paiement à venir</p>
              ) : (
                paiements_a_venir.slice(0, 5).map((paiement) => (
                  <div 
                    key={paiement.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50 border border-zinc-800"
                  >
                    <div>
                      <p className="text-sm font-medium text-zinc-200">{paiement.notes}</p>
                      <p className="text-xs font-mono text-zinc-500">{paiement.date_prevue}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-mono font-medium text-white">
                        {formatCurrency(paiement.montant, paiement.devise)}
                      </p>
                      <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400">
                        {paiement.type_paiement}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Missions récentes */}
        <Card className="bg-[#121214] border-zinc-800" data-testid="missions-recentes">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-zinc-500" />
              Missions récentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {missions_recentes.length === 0 ? (
                <p className="text-zinc-500 text-sm">Aucune mission récente</p>
              ) : (
                missions_recentes.map((mission) => (
                  <div 
                    key={mission.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50 border border-zinc-800"
                  >
                    <div>
                      <p className="text-sm font-medium text-zinc-200">{mission.titre}</p>
                      <p className="text-xs text-zinc-500">{mission.duree_mois} mois</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-mono font-medium text-white">
                        {formatCurrency(mission.montant_net || mission.montant_total || mission.montant_ttc || 0, mission.devise)}
                      </p>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          mission.statut === 'en_cours' 
                            ? 'border-emerald-700 text-emerald-400' 
                            : 'border-zinc-700 text-zinc-400'
                        }`}
                      >
                        {mission.statut}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section Innovation : Santé Financière & Alertes */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Score de Santé Financière */}
        {sante_financiere && (
          <HealthScore 
            score={sante_financiere.score} 
            niveau={sante_financiere.niveau} 
            composants={sante_financiere.composants}
          />
        )}

        {/* Alertes Intelligentes */}
        <AlertesWidget alertes={alertes} navigate={navigate} />
      </div>
    </div>
  );
}
